// ENG-10805 Part B: rendering a host's Claude `/usage` reading so the number cannot lie.
//
// WHY THIS IS A MODULE AND NOT A TEMPLATE STRING IN A PAGE. The columns this reads
// (`hosts.claude_usage_*`, written by ENG-9394's poller) are the accurate source that
// ENG-10805 Part A points the alerting cron at. The SAME numbers now want rendering in at
// least two places - the admin Spend page here, and the host view in ENG-10795 - and the
// history of this subsystem is that every independent re-derivation got something wrong:
// ENG-8631 (an agent reported "hit its weekly limit" for 58 hours at 13% usage), ENG-6379 (a
// reset date in 2027), ENG-9007/9120/9121 (the scope qualifier silently dropped). One model
// with its own tests is how a second surface stops being a second opinion.
//
// THE ONE RULE, from the ticket, and the reason every field below exists:
// **never render a bare percentage.** `claude_usage_state` and `claude_usage_read_at` exist
// precisely so a number arrives with its provenance. "72%" alone will eventually be a
// confident lie - it cannot distinguish a live reading from one frozen when a poller died.
// So this module has no code path that yields a percentage without an age beside it: the
// numbers live in `windows`, and `summary` is the only rendered sentence, and it always
// carries the age.
//
// TWO DELIBERATE NON-GOALS, both decided rather than skipped:
//
//  1. NO TREND, NO HISTORY. Migration 20260825000001 is explicit - "History is deliberately
//     not kept here" - so these columns are current-value-only. A number plus a freshness
//     stamp is free; a sparkline needs new storage and a retention decision. Out of scope
//     until someone asks for it and says where it lives.
//
//  2. NO THRESHOLD COLOURING. This model does not decide whether a percentage is "near the
//     limit", and that is not an oversight. The approaching threshold is environment-
//     configured and resolved server-side in `usage-limit-monitor`; duplicating it here
//     would create a SECOND source of truth about what counts as close to a ceiling, in a
//     subsystem whose entire failure history is disagreeing derivations. `tone` below is
//     about FRESHNESS only. If the Spend page later wants amber at 90%, the threshold should
//     be threaded in from the server rather than re-guessed here.

import {
  HOST_USAGE_POLL_INTERVAL_MS,
  READING_EXPIRY_INTERVALS,
  isReadingFresh,
} from '../claude-code-usage/host-usage-reading.js';

/** `hosts.claude_usage_state`: 'ok' | 'unknown' | 'not_applicable', CHECK-constrained. */
export type ClaudeUsageState = 'ok' | 'unknown' | 'not_applicable';

/** The `hosts` columns this model reads. Written by POST /host/usage-readings. */
export interface ClaudeUsageSignals {
  claude_usage_state: ClaudeUsageState | string | null;
  claude_usage_week_pct: number | null;
  claude_usage_week_resets_at: string | null;
  claude_usage_session_pct: number | null;
  claude_usage_session_resets_at: string | null;
  claude_usage_read_at: string | null;
}

/**
 * What we can honestly say about this host.
 *
 * `never_read` is separate from `unknown` on purpose. "The poller has never reported" and
 * "the poller reported and could not read the pane" are different facts with different
 * remedies, and collapsing them is the same conflation that ENG-10794 had to unpick for auth
 * (an epoch expiry read as `expired` when it meant sign-in never completed).
 */
export type ClaudeUsageKind = 'measured' | 'unknown' | 'not_applicable' | 'never_read';

export interface ClaudeUsageWindow {
  /** 'Weekly' or 'Session'. */
  label: 'Weekly' | 'Session';
  pct: number;
  /** 'resets Sep 24, 00:00 UTC', or '' when the row carries no instant for it. */
  resets: string;
}

export interface ClaudeUsageDisplay {
  kind: ClaudeUsageKind;
  /** The ONLY sentence a surface should render as the headline. Always carries the age. */
  summary: string;
  /** 'read 6 min ago' / 'last read 3 hours ago' / '' when never read. */
  age: string;
  /** Empty unless `kind === 'measured'`. Never render a `pct` without `age` in view. */
  windows: ClaudeUsageWindow[];
  /** The reading is old enough that it should not be leaned on. See STALE_READING_MS. */
  stale: boolean;
  /** FRESHNESS only - never a judgement about how high the percentage is. */
  tone: 'ok' | 'warn' | 'muted';
}

/**
 * When a reading stops being something to lean on.
 *
 * DELEGATED, not chosen here (Obi Wan on `#6333`). This module first defined its own 45
 * minutes - "three missed cycles", which sounded principled and was a THIRD number in a
 * subsystem that already had two. `hosts.claude_usage_read_at`'s own column comment names the
 * contract: consumers MUST refuse a reading older than ~2 poll intervals, and
 * `isReadingFresh` is the helper it points at. So this is now that bound, derived from the
 * producer's cadence rather than asserted alongside it.
 *
 * Kept as a named export because the tests pin it and a caller may want to explain the number
 * to a reader, but it is DERIVED - change `READING_EXPIRY_INTERVALS` and this follows.
 */
export const STALE_READING_MS = HOST_USAGE_POLL_INTERVAL_MS * READING_EXPIRY_INTERVALS;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Deterministic UTC rendering, e.g. 'Sep 24, 00:00 UTC'. Matches the console's other models. */
function formatUtc(ms: number): string {
  const d = new Date(ms);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.toISOString().slice(11, 16)} UTC`;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/**
 * 'read 6 minutes ago'. Returns '' when there is no clock or no usable instant - the caller
 * must then NOT show a percentage, which is why `buildClaudeUsageDisplay` treats an
 * unreadable `read_at` as `never_read` rather than as a measured reading with a blank age.
 *
 * `nowMs === null` is the server render and the first client render, which must agree, so
 * neither may read a clock (same rule as `usage-cap-banner-model`).
 */
function describeAge(readAtMs: number, nowMs: number): string {
  // FUTURE-DATED FIRST, before the clamp (CodeRabbit on `#6333`). `isReadingFresh` already
  // refuses a reading stamped ahead of the server, so `stale` goes true - but `Math.max(0, ...)`
  // below flattened the negative age to zero and the summary then read "read just now". The
  // display would have said `stale: true`, `tone: 'warn'` and "read just now" in one breath,
  // which is the two halves of one model disagreeing about the same fact.
  //
  // The api-side `describeAge` in usage-limit-monitor already got this right ("a NEGATIVE age
  // renders as 'clock skew' rather than a nonsensical -3m ago"); this is the same rule, and the
  // wording points at the clock rather than the poller because that is what is actually wrong.
  if (nowMs < readAtMs) return 'timestamp is in the future - the host clock looks wrong';
  const ageMs = nowMs - readAtMs;
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return 'read just now';
  if (minutes < 60) return `read ${plural(minutes, 'minute')} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `read ${plural(hours, 'hour')} ago`;
  return `read ${plural(Math.floor(hours / 24), 'day')} ago`;
}

function windowsFor(signals: ClaudeUsageSignals): ClaudeUsageWindow[] {
  const out: ClaudeUsageWindow[] = [];
  // Weekly first: it is the longer commitment and the one the alerting acts on.
  if (typeof signals.claude_usage_week_pct === 'number') {
    const at = signals.claude_usage_week_resets_at ? Date.parse(signals.claude_usage_week_resets_at) : NaN;
    out.push({
      label: 'Weekly',
      pct: signals.claude_usage_week_pct,
      resets: Number.isFinite(at) ? `resets ${formatUtc(at)}` : '',
    });
  }
  if (typeof signals.claude_usage_session_pct === 'number') {
    const at = signals.claude_usage_session_resets_at
      ? Date.parse(signals.claude_usage_session_resets_at)
      : NaN;
    out.push({
      label: 'Session',
      pct: signals.claude_usage_session_pct,
      resets: Number.isFinite(at) ? `resets ${formatUtc(at)}` : '',
    });
  }
  return out;
}

/**
 * Turn a host's reading into something that can be rendered without lying.
 *
 * `nowMs` may be null (server render / first client render). With no clock there is no age,
 * and with no age a percentage must not be shown - so a null clock degrades to a stated
 * "freshness unknown" rather than to a bare number.
 */
export function buildClaudeUsageDisplay(
  signals: ClaudeUsageSignals,
  nowMs: number | null,
): ClaudeUsageDisplay {
  const muted = { windows: [] as ClaudeUsageWindow[], stale: false, tone: 'muted' as const };

  if (signals.claude_usage_state === 'not_applicable') {
    // A host with no Claude subscription. NOT a failure, and not something to chase.
    return { kind: 'not_applicable', summary: 'No Claude subscription on this host', age: '', ...muted };
  }

  const readAtMs = signals.claude_usage_read_at ? Date.parse(signals.claude_usage_read_at) : NaN;
  if (!Number.isFinite(readAtMs)) {
    // The age of a failed poll is exactly what tells a never-polled host from a dead poller,
    // so a reading with no instant is reported as never read rather than as unknown.
    return { kind: 'never_read', summary: 'Usage never read on this host', age: '', ...muted };
  }

  // With a clock we can state the age; without one we say so rather than implying freshness.
  const age = nowMs === null ? '' : describeAge(readAtMs, nowMs);
  const agePhrase = age || 'freshness unknown until the page has a clock';
  // Via `isReadingFresh` rather than a bare subtraction, because it also refuses a
  // FUTURE-dated reading - a host whose clock never synced would otherwise read as
  // permanently fresh, which is the one way a staleness check can fail silently upward.
  const stale =
    nowMs !== null &&
    !isReadingFresh(new Date(readAtMs), new Date(nowMs), HOST_USAGE_POLL_INTERVAL_MS);

  if (signals.claude_usage_state !== 'ok') {
    // Schema invariant: a non-ok row carries no percentages (migration 20260825000001), so
    // there is nothing to render but the age - and the age is the useful part, because it
    // separates "the poller hiccuped once" from "it has been broken for a day".
    return {
      kind: 'unknown',
      summary: `Usage unknown, last ${agePhrase}`,
      age,
      windows: [],
      stale,
      tone: stale ? 'warn' : 'muted',
    };
  }

  const windows = windowsFor(signals);
  if (windows.length === 0) {
    // `ok` with no numbers should not happen, and if it does it is not a measurement.
    return { kind: 'unknown', summary: `Usage unknown, last ${agePhrase}`, age, windows: [], stale, tone: stale ? 'warn' : 'muted' };
  }

  // THE INVARIANT, in one expression: every percentage is emitted joined to the age.
  const figures = windows.map((w) => `${w.label.toLowerCase()} ${w.pct}%`).join(', ');
  return {
    kind: 'measured',
    summary: `${figures} - ${agePhrase}`,
    age,
    windows,
    stale,
    tone: stale ? 'warn' : 'ok',
  };
}

/**
 * The reading for a group of hosts sharing one `claude_account_email`.
 *
 * A Claude subscription is per-login, so hosts on the same email share one ceiling and should
 * report the same numbers. When they disagree it is staleness, not a real difference - so the
 * group's figure is the FRESHEST usable reading among its hosts, not the max or the mean.
 *
 * A max would be actively wrong here: it would let a host whose poller died at 100% pin the
 * whole subscription at 100% forever, which is the ENG-8631 failure (58 hours of a stale
 * "hit its weekly limit" at 13% actual) reproduced at the group level. Returns null when no
 * host has a usable reading, so a caller renders nothing rather than a zero.
 */
export interface GroupUsageSummary {
  /**
   * The subscription's reading, worded.
   *
   * `kind: 'not_applicable'` when no host in the group has a Claude subscription at all;
   * `'never_read'` when hosts could report but none ever has; `'unknown'` when they have read
   * but carry no usable figure - and in that case the AGE is the most recently read host's,
   * not an arbitrary one's.
   */
  display: ClaudeUsageDisplay;
  /**
   * Hosts whose OWN reading is not a fresh measurement - unknown, never read, or stale.
   *
   * This exists because `display` deliberately hides them: it reports the freshest reading, so
   * a host whose poller died is invisible in the headline. That is right for the number (the
   * ceiling is shared and the freshest reading is the best estimate of it) and wrong for
   * operations, because a dead poller is a real problem that the correct number conceals. So
   * the count is carried alongside rather than folded in.
   *
   * `not_applicable` hosts are NOT counted: a host with no Claude subscription is not failing
   * to report, it has nothing to report.
   */
  hostsNotReporting: number;
  /** Hosts that could report, i.e. excluding `not_applicable`. The denominator. */
  hostsExpected: number;
}

/**
 * Summarise a subscription group: one worded reading, plus how many of its hosts are not
 * actually reporting.
 *
 * Two facts, deliberately not merged into one. See `hostsNotReporting`.
 */
export function summariseGroupUsage(
  hosts: readonly ClaudeUsageSignals[],
  nowMs: number | null,
): GroupUsageSummary {
  const freshest = freshestClaudeUsage(hosts);
  const expected = hosts.filter((h) => h.claude_usage_state !== 'not_applicable');
  const notReporting = expected.filter((h) => {
    const d = buildClaudeUsageDisplay(h, nowMs);
    return d.kind !== 'measured' || d.stale;
  }).length;
  // NOTHING TO REPORT ON, which is not the same as failing to report (Obi Wan on `#6333`).
  //
  // Grouping is by `claude_account_email`, and `buildHostUsageReading` returns
  // `not_applicable` whenever the CLI reports a non-subscription login on a host that still
  // has an account email recorded - so an entire group can be `not_applicable`, and this is
  // reachable rather than theoretical. The previous code fell through to `hosts[0]` with the
  // state overwritten to `'unknown'`, and the page renders the summary unconditionally, so
  // such a group displayed "Claude usage: Usage unknown, last read N ago": a poller fault,
  // with an age, for a subscription that does not exist. That is exactly the
  // `unknown`-versus-`not_applicable` collapse this module's own docblock forbids and
  // ENG-10794 was filed about - `not_applicable` is not a failure and not something to chase,
  // so reporting it as one sends somebody looking for a remedy there isn't.
  if (expected.length === 0) {
    const base = buildClaudeUsageDisplay(
      { ...EMPTY_SIGNALS, claude_usage_state: 'not_applicable' },
      nowMs,
    );
    return {
      // Kind, tone and the rest come from the shared model so the group and host surfaces
      // cannot drift; only the noun changes, because "on this host" is the wrong word for a
      // group. "This account" is the accurate one - the group IS one `claude_account_email` -
      // and it reads correctly whether the group holds one host or ten.
      display: { ...base, summary: 'No Claude subscription on this account' },
      hostsNotReporting: 0,
      hostsExpected: 0,
    };
  }

  return {
    display: freshest
      ? buildClaudeUsageDisplay(freshest, nowMs)
      : // Hosts that COULD report, none with a usable reading. Worded through the same model
        // rather than with a bespoke string, so the group and host surfaces cannot drift.
        //
        // The host is chosen by most-recent `read_at`, NOT `expected[0]`. Array order came
        // from the SQL row order, so a group whose host A was last read three days ago and
        // host B one minute ago reported either age depending on which row came back first.
        // The age is the whole value of this line - the docblock above calls it what
        // "separates 'the poller hiccuped once' from 'it has been broken for a day'" - and an
        // order-dependent age cannot make that distinction.
        //
        // No forced `claude_usage_state` either. Every host here is already neither `ok` (or
        // `freshest` would have found it) nor `not_applicable` (filtered out above), so the
        // model reaches `unknown` on its own, and `never_read` when nothing has a parseable
        // instant - which is what this type's docblock has always promised and the override
        // was quietly preventing.
        buildClaudeUsageDisplay(mostRecentlyRead(expected) ?? EMPTY_SIGNALS, nowMs),
    hostsNotReporting: notReporting,
    hostsExpected: expected.length,
  };
}

/** A group with no hosts at all still has to word itself without throwing. */
const EMPTY_SIGNALS: ClaudeUsageSignals = {
  claude_usage_state: 'unknown',
  claude_usage_week_pct: null,
  claude_usage_week_resets_at: null,
  claude_usage_session_pct: null,
  claude_usage_session_resets_at: null,
  claude_usage_read_at: null,
};

export function freshestClaudeUsage<T extends ClaudeUsageSignals>(hosts: readonly T[]): T | null {
  // Only `ok` rows carry percentages (schema invariant), so only they can be the group's
  // FIGURE. The scan itself is shared with the no-figure fallback below, which needs the same
  // "most recently read" question asked of rows that are NOT ok.
  return mostRecentlyRead(hosts.filter((h) => h.claude_usage_state === 'ok'));
}

/**
 * The host read most recently, or null if none carries a parseable instant.
 *
 * Extracted so "most recently read" is implemented once. Both callers had to answer it and one
 * of them answered it with `[0]` instead - see `summariseGroupUsage` (Obi Wan on `#6333`).
 * Unparseable and absent instants are skipped rather than treated as epoch, which would make
 * a host with a corrupt timestamp win every comparison.
 */
function mostRecentlyRead<T extends ClaudeUsageSignals>(hosts: readonly T[]): T | null {
  let best: T | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const h of hosts) {
    const ms = h.claude_usage_read_at ? Date.parse(h.claude_usage_read_at) : NaN;
    if (!Number.isFinite(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = h;
    }
  }
  return best;
}
